import { tx as translateInterfaceText } from '@/utils/i18n';
import { HierarchicalFolder, FolderDetails, Video, FolderFilters } from '@/types/hierarchicalFolders';
import { authApi } from './authService';

// Helper function to get video metadata (dimensions and duration)
const getVideoMetadata = (file: File): Promise<{ width: number; height: number; duration: number }> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const url = URL.createObjectURL(file);
    video.src = url;
    video.preload = 'metadata';

    video.addEventListener('loadedmetadata', () => {
      const width = video.videoWidth;
      const height = video.videoHeight;
      const duration = video.duration;
      URL.revokeObjectURL(url);
      resolve({ width, height, duration });
    });

    video.addEventListener('error', () => {
      URL.revokeObjectURL(url);
      reject(new Error(translateInterfaceText("Erro ao carregar metadados do vídeo")));
    });
  });
};

// Recupera userId do storage (salvo no login)
function getUserId(): string {
  const loginChecked = localStorage.getItem('checkedLogin');
  const storage = loginChecked ? localStorage : sessionStorage;
  return storage.getItem('userId') || '';
}

// Converte para número aceitando os dois formatos que o backend pode devolver
// (camelCase agregado ou snake_case cru do aggregate de tb_metrica).
function pickNumber(...values: unknown[]): number {
  for (const value of values) {
    if (value === null || value === undefined || value === '') continue;
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
}

// Mappers: converte formato do backend (snake_case) → frontend (camelCase)
function mapFolder(raw: any): HierarchicalFolder {
  return {
    id: raw.id_folder,
    name: raw.folder_name,
    parentId: raw.parent_id ?? null,
    createdAt: raw.createdAt,
    childrenCount: raw.childrenCount || 0,
    videosCount: raw.videosCount || 0,
    // Métricas agregadas por pasta (soma dos vídeos), calculadas no backend
    totalPlays: pickNumber(raw.totalPlays, raw.total_plays, raw.plays),
    totalViews: pickNumber(raw.totalViews, raw.total_views, raw.views),
  };
}

function mapVideo(raw: any): Video {
  const rawStatus = raw.status || raw.videoStatus || raw.processing_status;
  const rawSharedId = raw.sharedId || raw.shared_id || raw.id_shared || '';
  const isShared = Boolean(rawSharedId && rawSharedId !== raw.id_video);
  const isProcessing = !isShared && (raw.isProcessing || raw.is_processing || rawStatus === 'processing');
  const status = isProcessing ? 'processing' : (rawStatus === 'error' || rawStatus === 'failed' ? 'error' : 'active');

  return {
    id: raw.id_video,
    title: raw.nomeVideo,
    duration: raw.duration || 0,
    status: status as any,
    createdAt: raw.createdAt,
    thumbnailUrl: raw.frame || raw.thumb || raw.thumbnailUrl || raw.thumbnail_url || raw.thumbnail || raw.imagemPause || raw.imagemInicial || raw.imagemFinal || raw.imageUrl || null,
    sharedId: rawSharedId,
    plays: raw.plays || 0,
    views: raw.views || 0,
    videoDownload: raw.videoDownload || raw.video_download || raw.videoMP4 || null,
    ...(raw.tracking ? { tracking: raw.tracking } : {}),
  } as any;
}

// Filtragem e ordenação client-side
function applyFilters(items: HierarchicalFolder[], filters?: FolderFilters): HierarchicalFolder[] {
  let filtered = [...items];

  if (filters?.q) {
    const query = filters.q.toLowerCase();
    filtered = filtered.filter(f => f.name.toLowerCase().includes(query));
  }

  if (filters?.sort) {
    filtered.sort((a, b) => {
      switch (filters.sort) {
        case 'az':
          return a.name.localeCompare(b.name);
        case 'za':
          return b.name.localeCompare(a.name);
        case 'recent':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'oldest':
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        case 'plays_desc':
          return (b.totalPlays || 0) - (a.totalPlays || 0);
        case 'plays_asc':
          return (a.totalPlays || 0) - (b.totalPlays || 0);
        case 'views_desc':
          return (b.totalViews || 0) - (a.totalViews || 0);
        case 'views_asc':
          return (a.totalViews || 0) - (b.totalViews || 0);
        default:
          return 0;
      }
    });
  }

  return filtered;
}

export const hierarchicalFoldersService = {
  // Listar pastas raiz
  async listRoot(filters?: FolderFilters): Promise<{
    items: HierarchicalFolder[];
    page: number;
    total: number;
  }> {
    const response = await authApi.get('/folders/root');
    const rawFolders: HierarchicalFolder[] = (response.data?.data || []).map(mapFolder);
    const filtered = applyFilters(rawFolders, filters);

    return {
      items: filtered,
      page: filters?.page || 1,
      total: filtered.length,
    };
  },

  // Buscar detalhes de uma pasta (filhos + vídeos + breadcrumb)
  async getFolderDetails(folderId: string, filters?: FolderFilters): Promise<FolderDetails> {
    const response = await authApi.get(`/folders/${folderId}`);
    const raw = response.data?.data;

    if (!raw) throw new Error(translateInterfaceText("Pasta não encontrada"));

    const folder = mapFolder(raw);
    const children: HierarchicalFolder[] = (raw.children || []).map(mapFolder);
    const filteredChildren = applyFilters(children, filters);
    const videos: Video[] = (raw.videos || []).map(mapVideo);
    const breadcrumb: HierarchicalFolder[] = (raw.breadcrumb || []).map(mapFolder);

    return {
      ...folder,
      children: filteredChildren,
      videos,
      breadcrumb,
    };
  },

  // Criar pasta
  async createFolder(name: string, parentId: string | null): Promise<HierarchicalFolder> {
    const response = await authApi.post('/folders', { name, parentId });
    return mapFolder(response.data?.data);
  },

  // Renomear pasta
  async renameFolder(folderId: string, newName: string): Promise<HierarchicalFolder> {
    const response = await authApi.patch(`/folders/${folderId}/name`, { name: newName });
    return mapFolder(response.data?.data);
  },

  // Deletar pasta (CASCADE nos filhos via banco)
  async deleteFolder(folderId: string): Promise<void> {
    await authApi.delete(`/folders/${folderId}`);
  },

  // Mover pasta para novo pai
  async moveFolder(folderId: string, newParentId: string | null): Promise<void> {
    await authApi.patch(`/folders/${folderId}/move`, { parentId: newParentId });
  },

  // Adicionar vídeos à pasta
  async addVideosToFolder(folderId: string, videoIds: string[]): Promise<void> {
    await authApi.post(`/folders/${folderId}/videos`, { videoIds });
  },

  // Remover vídeos da pasta (um por vez no backend)
  async removeVideosFromFolder(folderId: string, videoIds: string[]): Promise<void> {
    await Promise.all(
      videoIds.map(videoId => authApi.delete(`/folders/${folderId}/videos/${videoId}`))
    );
  },

  // Deletar múltiplas pastas
  async deleteFolders(folderIds: string[]): Promise<void> {
    await Promise.all(folderIds.map(id => authApi.delete(`/folders/${id}`)));
  },

  // Listar vídeos disponíveis para adicionar à pasta
  async listAvailableVideos(filters?: FolderFilters): Promise<{
    items: Video[];
    page: number;
    total: number;
  }> {
    const userId = getUserId();
    const response = await authApi.get(`/videos/get/${userId}`);
    const rawVideos = response.data || [];

    let mapped: Video[] = rawVideos.map(mapVideo);

    if (filters?.q) {
      const query = filters.q.toLowerCase();
      mapped = mapped.filter(v => v.title.toLowerCase().includes(query));
    }

    if (filters?.sort) {
      mapped.sort((a, b) => {
        switch (filters.sort) {
          case 'az': return a.title.localeCompare(b.title);
          case 'za': return b.title.localeCompare(a.title);
          case 'recent': return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
          default: return 0;
        }
      });
    }

    return {
      items: mapped,
      page: filters?.page || 1,
      total: mapped.length,
    };
  },

  // Buscar apenas os IDs de vídeos de uma pasta (sem enriquecimento — leve)
  async getVideoIdsInFolder(folderId: string): Promise<string[]> {
    const response = await authApi.get(`/folders/${folderId}`);
    const raw = response.data?.data;
    if (!raw) return [];
    const videos: any[] = raw.videos || [];
    return videos.map((v: any) => v.id_video).filter(Boolean);
  },

  // Resumo de plays de uma pasta a partir do endpoint de detalhes (dado real):
  // - ownVideosPlays: soma dos plays dos vídeos que estão diretamente na pasta
  // - recursivePlays: usa o agregado do backend (totalPlays) quando disponível;
  //   senão, soma os vídeos próprios + o agregado das subpastas diretas
  async getPlaysSummary(folderId: string): Promise<{
    ownVideosPlays: number;
    recursivePlays: number;
  }> {
    const response = await authApi.get(`/folders/${folderId}`);
    const raw = response.data?.data;
    if (!raw) return { ownVideosPlays: 0, recursivePlays: 0 };

    const ownVideosPlays = (raw.videos || []).reduce(
      (total: number, video: { plays?: number }) => total + pickNumber(video?.plays),
      0
    );

    const aggregated = pickNumber(raw.totalPlays, raw.total_plays);
    if (aggregated > 0) {
      return { ownVideosPlays, recursivePlays: aggregated };
    }

    const childrenPlays = (raw.children || []).reduce(
      (total: number, child: { totalPlays?: number; total_plays?: number }) =>
        total + pickNumber(child?.totalPlays, child?.total_plays),
      0
    );

    return { ownVideosPlays, recursivePlays: ownVideosPlays + childrenPlays };
  },

  // Upload de vídeos (já conectado à API real)
  async uploadVideos(files: File[], userId: string, onProgress?: (progress: number) => void): Promise<Video[]> {
    const uploadedVideos: Video[] = [];

    for (const file of files) {
      try {
        const videoMetadata = await getVideoMetadata(file);

        const formData = new FormData();
        formData.append('video', file);
        formData.append('inputedname', file.name.replace(/\.[^/.]+$/, ''));
        formData.append('VideoDuration', videoMetadata.duration.toString());
        formData.append('cor', 'rgba(1, 41, 187,0.75)');
        formData.append('corText', 'rgba(34, 34, 34, 0.75)');
        formData.append('borderWidth', '1');
        formData.append('corBarra', 'rgba(1, 41, 187,0.75)');
        formData.append('borderColor', 'rgba(1, 41, 187,0.75)');
        formData.append('haveTurboTest', 'false');
        formData.append('videoReady', 'false');
        formData.append('id', userId);
        formData.append('videoWidth', videoMetadata.width.toString());
        formData.append('videoHeight', videoMetadata.height.toString());
        // Tipo do vídeo: este fluxo sobe apenas VSL normal. Quando a VSL dinâmica
        // entrar, basta trazer este valor como 'true' (mesma rota upload-video-v2).
        const isDynamicVsl = 'false';
        formData.append('dynamic_vsl', isDynamicVsl);

        // O tipo vai também na query: o middleware de limite do trial roda ANTES do
        // parse do multipart, então lê ?dynamic_vsl (o body ainda não existe lá).
        const response = await authApi.post(`/videos/upload-video-v2/${userId}?dynamic_vsl=${isDynamicVsl}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 30 * 60 * 1000, // 30 min: internet lenta pode demorar para subir vídeos grandes
          onUploadProgress: (progressEvent) => {
            if (onProgress && progressEvent.total) {
              const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              onProgress(pct);
            }
          },
        });

        if (response.data.success && response.data.video) {
          const videoData = response.data.video;
          const video: Video = {
            id: videoData.id_video,
            title: videoData.nomeVideo || file.name.replace(/\.[^/.]+$/, ''),
            duration: videoData.duration || videoMetadata.duration,
            status: 'processing' as const,
            createdAt: videoData.createdAt || new Date().toISOString(),
            plays: 0,
            thumbnailUrl: videoData.frame || '/placeholder.svg',
          };
          uploadedVideos.push(video);
        }
      } catch (error) {
        console.error('Error uploading video:', error);
        throw error;
      }
    }

    return uploadedVideos;
  },
};
